import axios from 'axios';
import {accessTokenKey, authRedirectReason, refreshTokenKey, usernameKey,} from '../core/AuthInterceptor';
import {deleteCookie, setCookie} from '../utils/cookies';
import {AuthEntraExchangeResponse, CurrentUserProfileRequest} from "../models/admin-config.models";
import {env} from "../config/environment";
import {termsService} from "./terms.service";

const pendingTwoFactorEmailKey = 'pendingTwoFactorEmail';

export interface AuthenticationResponse {
    accessToken: string | null;
    refreshToken: string | null;
    username: string | null;
    twoFactorAuth: boolean;
    eulaAcceptanceRequired: boolean;
    eulaVersion: string;
}

export interface RegisterUserParamsType {
    firstName: string;
    lastName: string;
    position: string;
    phoneNumber: string;
    email: string;
    password: string;
    eulaAccepted: boolean;
    eulaVersion: string;
}

export interface RegisterAdminParamsType {
    email: string;
    password: string;
    confirmPassword: string;
    eulaAccepted: boolean;
    eulaVersion: string;
}

export const authService = {
    hasSystemAdminPrivilege: async (): Promise<boolean> => {
        const response = await axios.get<boolean>(`/users/has-system-admin`);
        return response.data;
    },

    registerUser: async (params: RegisterUserParamsType): Promise<void> => {
        await axios.post(
            `/auth/register`,
            params,
            {
                skipSessionExpiredHandling: true
            }
        );
    },

    registerAdmin: async (params: RegisterAdminParamsType): Promise<void> => {
        await axios.post(`/auth/register-system-admin`,
            params,
            {
                skipSessionExpiredHandling: true
            }
        );
    },

    loginRequest: async (
        email: string,
        password: string,
    ): Promise<AuthenticationResponse> => {
        const response = await axios.post<AuthenticationResponse>('/auth/login', { email, password });
        return response.data;
    },

    verifyTwoFactorAuthRequest: async (
        email: string,
        code: string,
    ): Promise<AuthenticationResponse> => {
        const response = await axios.post<AuthenticationResponse>('/auth/2fa/verify', { email, code });
        return response.data;
    },

    finalizeAuthentication: (
        response: Pick<AuthenticationResponse, 'accessToken' | 'refreshToken' | 'username'>,
        fallbackUsername: string,
    ): void => {
        if (!response.accessToken || !response.refreshToken) {
            throw new Error('Authentication response is missing tokens.');
        }

        setCookie(accessTokenKey, response.accessToken, 30);
        setCookie(refreshTokenKey, response.refreshToken, 30);
        setCookie(usernameKey, response.username ?? fallbackUsername, 30);
        localStorage.removeItem(authRedirectReason);
    },

    logout: (): void => {
        deleteCookie(accessTokenKey);
        deleteCookie(refreshTokenKey);
        deleteCookie(usernameKey);

        localStorage.removeItem(authRedirectReason);
        sessionStorage.removeItem(pendingTwoFactorEmailKey);

        termsService.clearPendingEulaVersion();

        window.location.href = '/login';
    },

    setPendingTwoFactorEmail: (email: string): void => {
        sessionStorage.setItem(pendingTwoFactorEmailKey, email);
    },

    getPendingTwoFactorEmail: (): string | null => {
        return sessionStorage.getItem(pendingTwoFactorEmailKey);
    },

    clearPendingTwoFactorEmail: (): void => {
        sessionStorage.removeItem(pendingTwoFactorEmailKey);
    },

    requestPasswordReset: async (email: string): Promise<void> => {
        await axios.post(`/auth/password-reset-request`, {
            email,
        });
    },

    resetPassword: async (token: string, newPassword: string): Promise<void> => {
        await axios.post(`/auth/password-reset`, {
            token,
            newPassword,
        });
    },

    setFirstPassword: async (token: string, newPassword: string): Promise<void> => {
        await axios.post(`/auth/password-reset`, {
            token,
            newPassword,
        });
    },

    activateAccount: async (activationToken: string): Promise<void> => {
        await axios.get('/auth/activate', {
            params: {activationToken: activationToken}
        });
    },

    uploadAvatar: async (file: File): Promise<void> => {
        const formData = new FormData();
        formData.append("avatar", file);

        await axios.patch("/auth/avatar", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    },

    updateCurrentUserData: async (profileData: CurrentUserProfileRequest): Promise<void> => {
        await axios.patch("/auth/profile", profileData);
    },

    updateCurrentUserPassword: async (currentPassword: string, newPassword: string): Promise<void> => {
        await axios.patch("/auth/change-password", {
            currentPassword,
            newPassword,
        });
    },

    fetchEntraLogin: (): void => {
        window.location.href = `${env.backendUrl}/auth/entra/login`;
    },

    exchangeEntraCode: async (code: string): Promise<AuthEntraExchangeResponse> => {
        const response = await axios.post<AuthEntraExchangeResponse>(`/auth/entra/exchange`, { code });
        return response.data;
    }
};